import {
  ceilPositiveMinutesToStep,
  SCHEDULING_MINUTE_STEP,
} from "../../../../../../../../../../../../../../../_shared/_logic/time-granularity"
import { normalizeTimeInput } from "../../../../../_shared/_logic/scheduling-events-series-planning"
import {
  MAX_BLOCKER_SERIES_OCCURRENCES,
  buildOccurrenceDays,
} from "../../../../../_shared/_logic/events-create-blocker-series-planning"
import type {
  BlockerConfigurationOutput,
  BlockerCreateInit,
  BlockerDurationMode,
  BlockerPlanningKind,
  BlockerSeriesPattern,
} from "../../../../../_shared/_logic/create-blocker-contract"

export const DEFAULT_BLOCKER_SERIES_WEEKDAYS = [0, 1, 2, 3, 4]
export const BLOCKER_WEEKDAY_LABELS = ["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"] as const

export type BlockerTimingState = {
  planningKind: BlockerPlanningKind
  durationMode: BlockerDurationMode
  singleDate: Date | null
  startTime: string
  durationMinutes: number | null
  seriesRangeStart: Date | null
  seriesRangeEnd: Date | null
  seriesPattern: BlockerSeriesPattern
  seriesWeekdays: number[]
  seriesIgnoreConflicts: boolean
}

type BlockerTimingValidationInput = Omit<BlockerTimingState, "seriesIgnoreConflicts">

const validDate = (value: unknown): Date | null => {
  if (!value) return null

  const parsed = value instanceof Date ? new Date(value) : new Date(String(value))

  return Number.isNaN(parsed.getTime()) ? null : parsed
}

export const normalizeBlockerCalendarDay = (value: unknown): Date | null => {
  const parsed = validDate(value)

  if (!parsed) return null

  parsed.setHours(0, 0, 0, 0)

  return parsed
}

export const addMonthsClamped = (value: Date, months: number): Date => {
  const base = new Date(value)
  const targetMonthIndex = base.getMonth() + months
  const targetYear = base.getFullYear() + Math.floor(targetMonthIndex / 12)
  const targetMonth = ((targetMonthIndex % 12) + 12) % 12
  const lastTargetDay = new Date(targetYear, targetMonth + 1, 0).getDate()
  base.setFullYear(targetYear, targetMonth, Math.min(base.getDate(), lastTargetDay))

  return base
}

const formatTime = (value: Date | null): string => {
  if (!value) return "09:00"

  return `${String(value.getHours()).padStart(2, "0")}:${String(value.getMinutes()).padStart(2, "0")}`
}

export const normalizeBlockerStartTime = (value: unknown): string | null => {
  const parsed = normalizeTimeInput(String(value ?? ""))

  if (!parsed) return null

  const totalMinutes = Math.min(
    23 * 60 + (60 - SCHEDULING_MINUTE_STEP),
    Math.ceil((parsed.hours * 60 + parsed.minutes) / SCHEDULING_MINUTE_STEP) *
      SCHEDULING_MINUTE_STEP,
  )

  return `${String(Math.floor(totalMinutes / 60)).padStart(2, "0")}:${String(totalMinutes % 60).padStart(2, "0")}`
}

export const normalizeBlockerDuration = (
  value: unknown,
  fallback: number | null = null,
): number | null => ceilPositiveMinutesToStep(value, fallback)

const initialDuration = (init: BlockerCreateInit): number => {
  const start = validDate(init.defaultStart)
  const end = validDate(init.defaultEnd)
  const difference = start && end ? (end.getTime() - start.getTime()) / 60_000 : null

  return normalizeBlockerDuration(difference, 60) ?? 60
}

export const buildInitialBlockerTimingState = ({
  init,
  output,
}: {
  init: BlockerCreateInit
  output?: BlockerConfigurationOutput
}): BlockerTimingState => {
  if (output) {
    return {
      planningKind: output.planningKind,
      durationMode: output.durationMode,
      singleDate: normalizeBlockerCalendarDay(output.singleDate),
      startTime: output.startTime,
      durationMinutes: output.durationMinutes,
      seriesRangeStart: normalizeBlockerCalendarDay(output.seriesRangeStart),
      seriesRangeEnd: normalizeBlockerCalendarDay(output.seriesRangeEnd),
      seriesPattern: output.seriesPattern,
      seriesWeekdays: [...output.seriesWeekdays],
      seriesIgnoreConflicts: output.seriesIgnoreConflicts,
    }
  }

  const today = normalizeBlockerCalendarDay(new Date()) as Date
  const configuredStart = validDate(init.defaultStart)
  const startDay = normalizeBlockerCalendarDay(configuredStart) ?? today

  return {
    planningKind: "series",
    durationMode: "all_day",
    singleDate: new Date(startDay),
    startTime: formatTime(configuredStart),
    durationMinutes: initialDuration(init),
    seriesRangeStart: new Date(startDay),
    seriesRangeEnd: addMonthsClamped(startDay, 3),
    seriesPattern: "WEEKLY",
    seriesWeekdays: [...DEFAULT_BLOCKER_SERIES_WEEKDAYS],
    seriesIgnoreConflicts: true,
  }
}

export const buildBlockerSeriesPreview = ({
  planningKind,
  seriesRangeStart,
  seriesRangeEnd,
  seriesPattern,
  seriesWeekdays,
}: Pick<
  BlockerTimingState,
  "planningKind" | "seriesRangeStart" | "seriesRangeEnd" | "seriesPattern" | "seriesWeekdays"
>): { count: number; error: string | null } | null => {
  if (planningKind !== "series" || !seriesRangeStart || !seriesRangeEnd) return null

  const result = buildOccurrenceDays({
    rangeStart: seriesRangeStart,
    rangeEnd: seriesRangeEnd,
    pattern: seriesPattern,
    weekdays: seriesWeekdays,
    maxOccurrences: MAX_BLOCKER_SERIES_OCCURRENCES,
  })

  return result.ok ? { count: result.days.length, error: null } : { count: 0, error: result.reason }
}

export const validateBlockerTiming = (input: BlockerTimingValidationInput): string | null => {
  if (input.planningKind !== "single" && input.planningKind !== "series") {
    return "Bitte Einzel- oder Serienplanung auswählen."
  }

  if (input.durationMode !== "all_day" && input.durationMode !== "individual") {
    return "Bitte Ganztägig oder Individuell auswählen."
  }

  if (input.planningKind === "single") {
    if (!normalizeBlockerCalendarDay(input.singleDate)) return "Bitte ein Datum wählen."
  } else {
    const occurrences = buildOccurrenceDays({
      rangeStart: input.seriesRangeStart,
      rangeEnd: input.seriesRangeEnd,
      pattern: input.seriesPattern,
      weekdays: input.seriesWeekdays,
      maxOccurrences: MAX_BLOCKER_SERIES_OCCURRENCES,
    })

    if (!occurrences.ok) return occurrences.reason
  }

  if (input.durationMode === "all_day") return null

  if (!normalizeBlockerStartTime(input.startTime)) return "Bitte eine gültige Start-Uhrzeit wählen."

  if (!normalizeBlockerDuration(input.durationMinutes)) return "Bitte eine gültige Dauer wählen."

  return null
}

export const blockerTimingStartsInPast = ({
  planningKind,
  durationMode,
  singleDate,
  startTime,
  seriesRangeStart,
  now = new Date(),
}: Pick<
  BlockerTimingState,
  "planningKind" | "durationMode" | "singleDate" | "startTime" | "seriesRangeStart"
> & {
  now?: Date
}): boolean => {
  const selectedDay = normalizeBlockerCalendarDay(
    planningKind === "series" ? seriesRangeStart : singleDate,
  )

  if (!selectedDay) return false

  if (durationMode === "all_day") {
    const today = normalizeBlockerCalendarDay(now)

    return Boolean(today && selectedDay.getTime() < today.getTime())
  }

  const normalizedTime = normalizeBlockerStartTime(startTime)

  if (!normalizedTime) return false

  const [hours, minutes] = normalizedTime.split(":").map(Number)
  selectedDay.setHours(hours, minutes, 0, 0)

  return selectedDay.getTime() < now.getTime()
}

const dateFormatter = new Intl.DateTimeFormat("de-DE", {
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
})

export const formatBlockerCalendarDay = (value: Date | null): string => {
  const day = normalizeBlockerCalendarDay(value)

  return day ? dateFormatter.format(day) : "nicht hinterlegt"
}
